import { Box, Card, CardContent, Skeleton, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { useGetConsumptionMonthlyTrendService } from "subModule/src/services/analytics/useAnalytics";
import { GetConsumptionMonthlyTrendResponse } from "subModule/src/services/analytics/interface";
import { BarChart } from "@mui/x-charts/BarChart";
import { InfiniteData } from "@tanstack/react-query";
import { TFunction } from "i18next";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";
interface MonthlyConsumptionTrendProps {
  themes: any;
  stores: { value: number; label: string }[];
  enqueueSnackbar: EnqueueSnackbar;
  storesLoaded: boolean;
  handleDropdownActions: (typeName: string, action: "searchIt" | "loadNext", search?: string) => void;
  setDropdownPage: any;
  storesData: InfiniteData<any, unknown> | undefined;
  searchLoading: string | null;
  dropdownPage: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { storeId: any; allStore?: boolean; range: any };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
export default function MonthlyConsumptionTrend(props: MonthlyConsumptionTrendProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const [data, setData] = useState<GetConsumptionMonthlyTrendResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { control, formState: { errors }, setValue, watch } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: 'monthly', label: t('mtd') },
    },
  });
  const filters = useMemo(() => [
    { value: 'daily', label: t('today') },
        { value: 'yesterday', label: t('yesterday') },
    { value: 'weekly', label: t('wtd') },
    { value: 'monthly', label: t('mtd') },
    { value: 'last_month', label: t('last_month') },
  ], [i18n.language]);
  const values = watch(["storeId", "range"]);
  const selectedStoreValue = Number(values[0]?.value ?? values[0]);
  const isAllStoreSelected = selectedStoreValue === 0 || props.globalFilters.allStore === true;
  const { refetch: apiRefetch } = useGetConsumptionMonthlyTrendService({
    storeId: isAllStoreSelected ? null : (values[0]?.value || null),
    allStore: isAllStoreSelected,
    range: values[1]?.value || null,
  });
  const compactSelectSx = {
    "& .css-7896aj-control, & .css-1xiok1u-control": {
      minHeight: "auto",
      fontSize: '9px',
      height: "auto",
      overflow: "hidden",
      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer": {
        background: "#fff",
        padding: 0,
        width: "15px",
        height: "15px",
      },
      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator": { position: "absolute", right: 0 },
      border: 0,
    },
  };
  useEffect(() => {
    if (firstRender.current && props.storesLoaded === true) {
      firstRender.current = false;
      const defaultStore = (props?.stores?.length > 1 ? props?.stores?.[1] : props?.stores?.[0]) || null;
      setValue("storeId", defaultStore);
      setValue("range", props.globalFilters.range);
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stores, props.storesLoaded]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find(f => f.value === currentRange?.value);
    if (updated) setValue("range", updated);
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true && props?.globalFilters?.range?.value !== 'yearly') {
      setValue("storeId", props.globalFilters.storeId);
      setValue("range", props.globalFilters.range);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || null);
        } else {
          props.enqueueSnackbar(<AlertMessages text={res?.data?.message || "Something went wrong"} />, { variant: "error" });
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true) props.setGlobalFilterApplied(false);
      });
  };
  const timeSeries = data?.timeSeries || [];
  return (
    <Card sx={{ height: "285px", boxShadow: "none", borderRadius: "8px", border: '1px solid #E5E7EB', "& .MuiCardContent-root": { padding: "16px !important" } }}>
      <CardContent>
        <Box sx={dashboardWidgetHeaderSx}>
          <Box sx={dashboardWidgetTitleRowSx}>
                    <Typography variant="h1" gutterBottom className="font-Saira text-start mb-0 mt-[5px]" sx={{ color: props.themes?.primary, fontSize: { xs: 12, sm: 14 }, fontWeight: { xs: 600, sm: 600 }, textAlign: "left" }}>
            {t("ConsumptionTrend")}
          </Typography>
                    </Box>
                    <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="storeId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stores || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.storeId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("store")}
                    isLoading={props.searchLoading === "monthlyConsumptionTrend"}
                    onInputChange={(search: string, { action }: { action: string }) => {
                      if (action === "input-change" || (action !== "set-value" && action !== "menu-close" && action !== "input-blur")) {
                        props.handleDropdownActions("monthlyConsumptionTrend", "searchIt", search);
                      } else if (action === "input-blur" || action === "menu-close") {
                        if (props.dropdownSearchTerm || props.dropdownPage > 1) props.handleDropdownActions("monthlyConsumptionTrend", "searchIt", "");
                        else props.setDropdownSearchTerm("");
                        props.setDropdownPage(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.storesData?.pages[0]?.data as any)?.last) props.handleDropdownActions("monthlyConsumptionTrend", "loadNext");
                    }}
                    page={props.dropdownPage}
                    isSearchable
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
        {loading ? (
          <>
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary, marginTop: "5px" }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
            <Skeleton animation="pulse" sx={{ backgroundColor: props?.themes?.secondary }} />
          </>
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <MonthlyConsumptionChart timeSeries={timeSeries} t={t} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
const MonthlyConsumptionChart = ({
  timeSeries,
  t,
}: {
  timeSeries: { date: number; dateLabel: string; consumption: number }[];
  t: TFunction<"translation", undefined>;
}) => {
  const isLargeScreen = useMediaQuery(`@media('min-width: 1200px')`);
  const formatted = (timeSeries || [])
    .slice()
    .sort((a, b) => a.date - b.date)
    .map((x) => ({
      dateLabel: x.dateLabel,
      consumption: Number(x.consumption || 0),
    }));
  return (
    <Box sx={{ minWidth: 600 }}>
      <BarChart
        height={220}
        xAxis={[
          {
            data: formatted.map((x) => x.dateLabel),
            scaleType: "band",
          },
        ]}
        series={[
          {
            data: formatted.map((x) => x.consumption),
            label: t("consumption"),
          },
        ]}
        colors={["#2F80ED"]}
        yAxis={[
          {
            valueFormatter: (val) =>
              new Intl.NumberFormat("en", {
                notation: "compact",
                compactDisplay: "short",
                maximumFractionDigits: 2,
              }).format(Number(val)),
          },
        ]}
        skipAnimation={false}
        margin={{ left: 40, right: isLargeScreen ? 0 : 10, top: 15, bottom: 35 }}
        grid={{ horizontal: true }}
        slotProps={{
          legend: { hidden: true },
          noDataOverlay: {
            message: t("noRecordFound"),
          },
        }}
      />
    </Box>
  );
};
